import React, { useState } from 'react'
import styled from 'styled-components'
import { contains, path, without } from '../../lib/ramda'
import { FormFieldLabel } from '../FormFieldLabel'
import { SelectionItem } from './SelectionItem'
import { SelectionFieldType } from './SelectionFieldType'
import { SelectionType } from './SelectionType'
import { ISelectionItemProps } from './SelectionItemProp'

const StyledSelectionGroup = styled.div`
  position: relative;
`

const SelectionGroupContent = styled.div`
  margin-top: 0.25rem;
`

interface ISelectionGroupProps {
  initItems: Array<ISelectionItemProps>
  value?: string | Array<string>
  onChange?: (value: string | Array<string>) => void
  labelText?: string
  validationMessage?: string
  required?: boolean
  multiSelect?: boolean
  isInline?: boolean
}

const SelectionGroup: React.FC<ISelectionGroupProps> = ({
  labelText,
  validationMessage,
  required = false,
  initItems,
  value,
  isInline = false,
  onChange,
  multiSelect = false,
}) => {
  const [items, setItems] = useState(initItems)
  const selectedIds = items
    .filter(({ value: v }) => contains(v, [].concat(value)))
    .map(({ id }) => id)

  const updateSelectedIds = (newSelectedIds) => {
    const newSelectedItems = items.filter(({ id: currentId }) =>
      contains(currentId, newSelectedIds)
    )
    const newSelectedValues = newSelectedItems.map(
      ({ text, value: val }) => val || text
    )

    //setSelectedIds(newSelectedIds);
    if (multiSelect) {
      onChange(newSelectedValues)
    } else {
      onChange(path([0], newSelectedValues) || '')
    }
  }

  const addSelectedId = (id) => {
    if (multiSelect) {
      updateSelectedIds(selectedIds.concat(id))
    } else {
      updateSelectedIds(id)
    }
  }

  // only used by multi
  const removeSelectedId = (id) => {
    updateSelectedIds(without([id], selectedIds))
  }

  const selectItem = (isSelected, id) => {
    if (multiSelect) {
      if (isSelected) {
        addSelectedId(id)
        return
      }
      removeSelectedId(id)
    } else {
      addSelectedId(id)
    }
  }

  return (
    <StyledSelectionGroup>
      <FormFieldLabel
        labelText={labelText}
        validationMessage={validationMessage}
        required={required}
      />
      <SelectionGroupContent>
        {items.map(
          (
            {
              text,
              children,
              placeholder,
              fieldType = SelectionFieldType.NORMAL,
              id,
              value: v,
            },
            itemIndex
          ) => (
            <SelectionItem
              key={id}
              isInline={isInline}
              type={multiSelect ? SelectionType.CHECKBOX : SelectionType.RADIO}
              fieldType={fieldType}
              value={v}
              selected={contains(id, selectedIds)}
              placeholder={placeholder}
              onChange={(ev, isSelected) => {
                if (
                  ev.target.type === 'checkbox' ||
                  ev.target.type === 'radio'
                ) {
                  selectItem(isSelected, id)
                } else if (ev.target.type === 'text') {
                  // refresh items
                  const newValue = ev.target.value
                  const newItems = [].concat(items)
                  newItems[itemIndex].value = newValue
                  setItems(newItems)
                  selectItem(newValue.length > 0, id)
                }
              }}
            >
              {text || children}
            </SelectionItem>
          )
        )}
      </SelectionGroupContent>
    </StyledSelectionGroup>
  )
}

export { SelectionGroup }
